import { useMaxFileUpload } from '#pplx/frontend/apps/ask/hooks/eppo-hooks/useMaxFileUpload.js';
import { uploadFilesOrImagesMessage } from '#pplx/frontend/apps/ask/hooks/file-attachments-hooks/fileAttachmentMessages.js';
import { useGateUploadFile } from '#pplx/frontend/apps/ask/hooks/file-attachments-hooks/useGateUploadFile.js';
import { useThreadConfig } from '#pplx/frontend/apps/ask/providers/ThreadSettingsProviderShared.js';
import { useUserSettingsQuery } from '#pplx/frontend/apps/ask/user-settings/hooks/useUserSettingsQuery.js';
import { SearchMode } from '#pplx/frontend/ask-models/types/search-modes.js';
import { useIsSubscribed } from '#pplx/frontend/auth/useIsSubscribed.js';
import { assertUnreachableValue } from '#pplx/frontend/common/type_utils/unreachableValue.js';
import { FALLBACK_UPLOAD_DAILY_LIMIT_FREE, FALLBACK_UPLOAD_DAILY_LIMIT_SUBSCRIBED, } from '#pplx/frontend/components/utils/consts.js';
import { ASI_FILE_UPLOAD_ACCEPTED_FILE_TYPES, ASI_FILE_UPLOAD_MAX_FILE_SIZE_MB, ASI_FILE_UPLOAD_TRANSPORT, } from '#pplx/frontend/file-uploads/asiFileUploadConfig.js';
import { FILE_RETENTION_DAYS } from '#pplx/frontend/file-uploads/fileRetentionMessage.js';
import { useCallback, useEffect, useMemo, useState } from 'react';
/**
 * Resolves the surface mode from the selected source, then resolves the upload policy mode from
 * `uploadSearchModeOverride` or that surface mode.
 */
export function resolveAskInputAttachmentUploadContext({ querySource, searchModeSource, uploadSearchModeOverride, }) {
    let resolvedSearchMode;
    switch (searchModeSource.kind) {
        case 'search-model':
            resolvedSearchMode = searchModeSource.getSearchModeForSearchModel(searchModeSource.searchModel);
            break;
        case 'thread-search-mode':
            resolvedSearchMode = searchModeSource.threadSearchMode;
            break;
        default:
            assertUnreachableValue(searchModeSource);
    }
    return {
        reason: `ask-input-inner-${querySource}`,
        resolvedSearchMode,
        uploadSearchMode: uploadSearchModeOverride ?? resolvedSearchMode,
    };
}
export function useAttachmentUploadPolicy({ reason, searchMode, menuPolicy, }) {
    const maxFiles = useMaxFileUpload();
    const { connectorLimits } = useUserSettingsQuery({ reason });
    const { uploadRateLimit, setUploadRateLimit } = useThreadConfig();
    const { hasActiveSubscription } = useIsSubscribed();
    const [fallbackQuotaConsumed, setFallbackQuotaConsumed] = useState(0);
    const isAsi = searchMode === SearchMode.ASI;
    useEffect(() => {
        if (uploadRateLimit !== undefined)
            setFallbackQuotaConsumed(0);
    }, [uploadRateLimit]);
    const fallbackQuota = hasActiveSubscription
        ? FALLBACK_UPLOAD_DAILY_LIMIT_SUBSCRIBED
        : FALLBACK_UPLOAD_DAILY_LIMIT_FREE;
    const remainingUploadCount = isAsi
        ? undefined
        : (uploadRateLimit ?? Math.max(fallbackQuota - fallbackQuotaConsumed, 0));
    const consumeUploadCount = useCallback((uploadedFileCount) => {
        if (isAsi)
            return;
        if (uploadRateLimit === undefined) {
            setFallbackQuotaConsumed((consumed) => Math.min(consumed + uploadedFileCount, fallbackQuota));
        }
        setUploadRateLimit(uploadedFileCount);
    }, [fallbackQuota, isAsi, setUploadRateLimit, uploadRateLimit]);
    const gate = useGateUploadFile({ uploadRateLimit: remainingUploadCount });
    const constraints = useMemo(() => isAsi
        ? {
            acceptedFileTypes: [ASI_FILE_UPLOAD_ACCEPTED_FILE_TYPES],
            maxFiles,
            maxFileSizeMB: connectorLimits?.max_attachment_file_size_mb_by_source?.asi ??
                ASI_FILE_UPLOAD_MAX_FILE_SIZE_MB,
        }
        : {
            maxFiles,
            maxFileSizeMB: connectorLimits?.max_attachment_file_size_mb,
        }, [connectorLimits, isAsi, maxFiles]);
    const transport = useMemo(() => (isAsi ? ASI_FILE_UPLOAD_TRANSPORT : { skipParsing: false }), [isAsi]);
    return useMemo(() => ({
        constraints,
        gate: {
            canUploadFile: gate.canUploadFile,
            checkUploadFile: gate.checkUploadFile,
        },
        quota: { consumeUploadCount, remainingUploadCount },
        retentionDays: menuPolicy ? menuPolicy.retentionDays : FILE_RETENTION_DAYS,
        menuMessage: menuPolicy?.menuMessage ?? uploadFilesOrImagesMessage,
        transport,
    }), [
        constraints,
        consumeUploadCount,
        gate.canUploadFile,
        gate.checkUploadFile,
        remainingUploadCount,
        menuPolicy,
        transport,
    ]);
}
//# sourceMappingURL=useAttachmentUploadPolicy.js.map